(function() {
  'use strict';
  
  /**
   * YISE Beauty - Click Tracking → GA4 Direct (Frontend)
   * Uses gtag() to send events - no API secret needed
   * Respects Shopify customer privacy / consent settings
   */
  
  // ==========================================================================
  // CONFIGURATION
  // ==========================================================================
  
  const GA4_MEASUREMENT_ID = 'G-HW5E9THQDG';  // Your GA4 Measurement ID
  const DEBUG = false;  // Set to false in production
  const DEDUPE_WINDOW_MS = 2000;  // Ignore duplicate events within 2 seconds
  
  // ==========================================================================
  // DEDUPLICATION
  // ==========================================================================
  
  // Cache of recent events to prevent duplicates
  const recentEvents = new Map();
  const recentGwpCartStates = new Map();
  
  // Track pending select events (to fire after a delay)
  const pendingSelectEvents = new Map();
  const selectEventTimers = new Map();
  
  /**
   * Generate a unique key for an event based on its properties
   */
  function getEventKey(eventName, params) {
    // Create a key from the most important identifying properties
    const keyParts = [
      eventName,
      params.custom_click_name || '',
      params.custom_button_text || '',
      params.custom_element_type || '',
      params.custom_click_id || '',
      params.custom_element_id || '',
      params.page_path || '',
      params.custom_product_handle || '',
      params.custom_variant_id || '',
      params.custom_gwp_offer_key || ''
    ];
    if (eventName === 'gwp_cart_state') {
      keyParts.push(
        params.custom_gwp_qualified || '',
        params.custom_gwp_present || '',
        params.custom_gwp_quantity || '',
        params.custom_gwp_threshold_gap_band || '',
        params.custom_surface || ''
      );
    }
    return keyParts.join('|');
  }
  
  /**
   * Check if this event is a duplicate of a recent event
   */
  function isDuplicateEvent(eventName, params) {
    const eventKey = getEventKey(eventName, params);
    const now = Date.now();
    if (eventName === 'gwp_cart_state') {
      const scope = JSON.stringify([params.custom_gwp_offer_key || '', params.page_path || '']);
      for (const [key, value] of recentGwpCartStates) {
        if (now - value.timestamp > DEDUPE_WINDOW_MS) recentGwpCartStates.delete(key);
      }
      const previous = recentGwpCartStates.get(scope);
      if (previous?.eventKey === eventKey && now - previous.timestamp < DEDUPE_WINDOW_MS) return true;
      recentGwpCartStates.set(scope, { eventKey, timestamp: now });
      return false;
    }
    
    // Clean up old entries (older than dedupe window)
    for (const [key, timestamp] of recentEvents.entries()) {
      if (now - timestamp > DEDUPE_WINDOW_MS) {
        recentEvents.delete(key);
      }
    }
    
    // Check if this event was sent recently
    if (recentEvents.has(eventKey)) {
      const lastSent = recentEvents.get(eventKey);
      if (now - lastSent < DEDUPE_WINDOW_MS) {
        return true;
      }
    }
    
    // Record this event
    recentEvents.set(eventKey, now);
    return false;
  }
  
  // ==========================================================================
  // CONSENT HANDLING
  // ==========================================================================
  
  function isAnalyticsAllowed() {
    if (window.Shopify && window.Shopify.customerPrivacy) {
      if (typeof Shopify.customerPrivacy.analyticsProcessingAllowed === 'function') {
        const allowed = Shopify.customerPrivacy.analyticsProcessingAllowed();
        return allowed;
      }
      
      if (typeof Shopify.customerPrivacy.currentVisitorConsent === 'function') {
        const consent = Shopify.customerPrivacy.currentVisitorConsent();
        if (consent && consent.analytics !== undefined) {
          const allowed = consent.analytics === 'yes' || consent.analytics === true;
          return allowed;
        }
      }
    }
    
    // If no consent API available, default to allowed (pre-consent regions)
    return true;
  }
  
  // ==========================================================================
  // GA4 GTAG SETUP
  // ==========================================================================
  
  function initGtag() {
    // Skip if already initialized
    if (window.gtag) {
      return;
    }
    
    // Skip if not configured
    if (GA4_MEASUREMENT_ID.includes('XXXXXXXX')) {
      console.warn('[GA4] Not configured - please update GA4_MEASUREMENT_ID');
      return;
    }
    
    // Initialize dataLayer
    window.dataLayer = window.dataLayer || [];
    window.gtag = function() {
      window.dataLayer.push(arguments);
    };
    
    gtag('js', new Date());
    gtag('config', GA4_MEASUREMENT_ID, {
      send_page_view: false  // Prevent duplicate page views
    });
    
    // Shopify/GTM already loads this GA4 property on the storefront. Queue
    // events immediately, but avoid downloading a duplicate runtime. Keep a
    // delayed fallback so tracking still works if that integration is removed.
    window.setTimeout(function() {
      const existingGtagScript = document.querySelector(
        'script[src*="googletagmanager.com/gtag/js"][src*="id=' + GA4_MEASUREMENT_ID + '"]'
      );

      if (existingGtagScript) {
        return;
      }

      const script = document.createElement('script');
      script.async = true;
      script.src = 'https://www.googletagmanager.com/gtag/js?id=' + GA4_MEASUREMENT_ID;
      script.onerror = function() {
        console.error('[GA4] Failed to load gtag.js fallback');
      };
      document.head.appendChild(script);
    }, 10000);
  }
  
  /**
   * Send event to GA4 via gtag
   */
  function sendToGA4(eventName, params, userData) {
    // Check consent before sending
    if (!isAnalyticsAllowed()) {
      console.warn('[GA4] Event blocked by consent:', eventName);
      return;
    }
    
    // Load GA4 only when the first consented event actually needs to be sent.
    // The queue preserves that first event while gtag.js downloads.
    if (!window.gtag) {
      initGtag();
    }

    if (!window.gtag) {
      return;
    }
    
    // Clean params - convert all values to strings
    const cleanedParams = {};
    for (const [key, value] of Object.entries(params)) {
      // Only skip null/undefined, but allow empty strings and 0/false
      if (value === null || value === undefined) continue;
      const cleanKey = String(key).replace(/[^a-zA-Z0-9_]/g, '_').substring(0, 40);
      if (typeof value === 'string') {
        // Special handling for custom_button_text - ensure it's cleaned
        if (key === 'custom_button_text') {
          const cleaned = cleanText(value);
          if (cleaned !== null) {
            cleanedParams[cleanKey] = cleaned;
          }
        } else {
          // Allow empty strings, but trim and limit length
          const trimmed = value.trim();
          if (trimmed.length > 0) {
            cleanedParams[cleanKey] = trimmed.substring(0, 100);
          }
        }
      } else if (typeof value === 'number' || typeof value === 'boolean') {
        // Convert numbers and booleans to strings
        cleanedParams[cleanKey] = String(value);
      }
    }
    
    // Check for duplicates before sending
    if (isDuplicateEvent(eventName, cleanedParams)) {
      return;
    }

    if (userData && /^[a-f0-9]{64}$/.test(userData.sha256_email_address || '')) {
      cleanedParams.user_data = {
        sha256_email_address: userData.sha256_email_address
      };
    }
    
    gtag('event', eventName, cleanedParams);
    
    if (DEBUG) {
      console.log('[GA4] ✓ Event sent:', eventName, cleanedParams);
    }
  }
  
  // ==========================================================================
  // CLICK TRACKING HELPERS
  // ==========================================================================
  
  function getProductHandle(url) {
    if (!url) return null;
    const match = url.match(/\/products\/([^?#/]+)/);
    return match ? match[1] : null;
  }
  
  function getVariantId(url) {
    if (!url) return null;
    try {
      const urlObj = new URL(url, window.location.origin);
      return urlObj.searchParams.get('variant');
    } catch (e) {
      return null;
    }
  }
  
  function getSectionInfo(element) {
    const section = element.closest('.shopify-section');
    if (!section || !section.id) {
      return { sectionId: null, sectionHandle: null };
    }
    
    let sectionId = section.id;
    
    // Remove "shopify-section-" prefix if present
    if (sectionId.startsWith('shopify-section-')) {
      sectionId = sectionId.replace('shopify-section-', '');
    }
    
    // Remove "template--{template-id}__" prefix if present (e.g., "template--18667346755817__slideshow_RJ6AiX")
    if (sectionId.includes('template--') && sectionId.includes('__')) {
      sectionId = sectionId.replace(/^template--\d+__/, '');
    }
    
    // For section handle, extract just the final part after the last __
    const handleMatch = sectionId.match(/__([^_]+)$/);
    const sectionHandle = handleMatch ? handleMatch[1] : sectionId;
    
    return { sectionId, sectionHandle };
  }
  
  /**
   * Get data attribute value, checking both dataset and getAttribute
   */
  function getDataAttribute(element, attrName) {
    if (!element) return null;
    
    let current = element;
    while (current && current !== document.body) {
      // Try dataset first (camelCase: data-click-id becomes clickId)
      if (current.dataset) {
        const camelCaseKey = attrName.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
        if (current.dataset[camelCaseKey]) {
          return current.dataset[camelCaseKey];
        }
      }
      
      // Fallback to getAttribute (handles all cases)
      const attrValue = current.getAttribute('data-' + attrName);
      if (attrValue !== null && attrValue !== '') {
        return attrValue;
      }
      
      current = current.parentElement;
    }
    return null;
  }

  function getClickName(element) {
    // First try data-click-name
    const clickName = getDataAttribute(element, 'click-name');
    if (clickName) return clickName;
    
    // Fallback to other methods
    if (element.getAttribute('aria-label')) return element.getAttribute('aria-label');
    
    const text = element.textContent?.trim();
    if (text && text.length > 0 && text.length < 100) {
      return text.substring(0, 50);
    }
    
    // Safely get className (handles SVG elements where className is an object)
    const className = element.className;
    const classStr = typeof className === 'string' ? className : (className?.baseVal || '');
    return element.tagName.toLowerCase() + (classStr ? '.' + classStr.split(' ')[0] : '');
  }

  function getClickId(element) {
    return getDataAttribute(element, 'click-id');
  }

  function getClickLinkName(element) {
    return getDataAttribute(element, 'click-link-name');
  }

  function getClickProduct(element) {
    return getDataAttribute(element, 'click-product');
  }

  function getClickVariant(element) {
    return getDataAttribute(element, 'click-variant');
  }

  function getClickProductId(element) {
    return getDataAttribute(element, 'click-productid');
  }

  function getClickVariantId(element) {
    return getDataAttribute(element, 'click-variantid');
  }

  function getElementId(element) {
    if (element.id) return element.id;
    
    let current = element.parentElement;
    while (current && current !== document.body) {
      if (current.id) return current.id;
      current = current.parentElement;
    }
    return null;
  }
  
  /**
   * Get the selected variant for a product based on current selections
   */
  function getSelectedVariantForProduct(element, productId) {
    if (!productId) return null;
    
    // Find the variant data script
    const variantsDataScript = document.querySelector(`script[data-product-variants="${productId}"]`);
    if (!variantsDataScript) return null;
    
    let variantsData;
    try {
      variantsData = JSON.parse(variantsDataScript.textContent);
    } catch (e) {
      console.error('[GA4] Failed to parse variant data:', e);
      return null;
    }
    
    // Find the variant dropdowns container for this product
    const container = element.closest('.pdp-cross-sell__variant-dropdowns');
    if (!container) return null;
    
    // Get all selected options
    const selectedOptions = [];
    
    // Get values from swatch groups
    const swatchGroups = container.querySelectorAll('.pdp-cross-sell__swatch-group');
    swatchGroups.forEach(group => {
      const optionIndex = parseInt(group.getAttribute('data-option-index'));
      const checkedInput = group.querySelector('.pdp-cross-sell__swatch-input:checked');
      if (checkedInput) {
        selectedOptions[optionIndex] = checkedInput.value;
      }
    });
    
    // Get values from select dropdowns
    const selects = container.querySelectorAll('.pdp-cross-sell__variant-select');
    selects.forEach(select => {
      const optionIndex = parseInt(select.getAttribute('data-option-index'));
      selectedOptions[optionIndex] = select.value;
    });
    
    // Find matching variant
    const matchingVariant = variantsData.find(variant => {
      return (
        (!selectedOptions[0] || variant.option1 === selectedOptions[0]) &&
        (!selectedOptions[1] || variant.option2 === selectedOptions[1]) &&
        (!selectedOptions[2] || variant.option3 === selectedOptions[2])
      );
    });
    
    return matchingVariant;
  }
  
  /**
   * Clean text: remove HTML, trim whitespace, remove line breaks, ellipsis at 40 chars
   */
  function cleanText(text) {
    if (!text) return null;
    
    // Create a temporary element to strip HTML tags
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = text;
    
    // Remove script and style tags content
    const scripts = tempDiv.querySelectorAll('script, style');
    scripts.forEach(script => script.remove());
    
    let cleaned = tempDiv.textContent || tempDiv.innerText || '';
    
    // Remove ALL types of line breaks and whitespace characters
    cleaned = cleaned
      .replace(/[\r\n\t\f\v]+/g, ' ')  // Remove \r, \n, \t, \f, \v
      .replace(/\u00A0/g, ' ')         // Replace non-breaking spaces
      .replace(/\s+/g, ' ')            // Normalize multiple spaces to single space
      .trim();
    
    // Filter out CSS-like content (contains { or :)
    if (cleaned.includes('{') || cleaned.match(/^\s*--[\w-]+\s*:/)) {
      return null;
    }
    
    // Ellipsis at 40 characters
    if (cleaned.length > 40) {
      cleaned = cleaned.substring(0, 40) + '...';
    }
    
    return cleaned || null;
  }
  
  function getElementText(element) {
    let rawText = null;
    
    if (element.tagName === 'SELECT') {
      const selectedOption = element.options[element.selectedIndex];
      rawText = selectedOption ? selectedOption.text : null;
    } else if (element.type === 'radio' || element.type === 'checkbox') {
      const label = document.querySelector(`label[for="${element.id}"]`);
      if (label) {
        // Clone the label to avoid modifying the DOM
        const labelClone = label.cloneNode(true);
        // Remove script and style tags
        labelClone.querySelectorAll('script, style').forEach(el => el.remove());
        rawText = labelClone.textContent || labelClone.innerText;
      } else {
        const parentLabel = element.closest('label');
        if (parentLabel) {
          // Clone the label to avoid modifying the DOM
          const labelClone = parentLabel.cloneNode(true);
          // Remove script and style tags
          labelClone.querySelectorAll('script, style').forEach(el => el.remove());
          rawText = labelClone.textContent || labelClone.innerText;
        }
      }
    } else {
      // Clone element to avoid modifying the DOM
      const clone = element.cloneNode(true);
      // Remove script and style tags
      clone.querySelectorAll('script, style').forEach(el => el.remove());
      
      // Try to get text content first
      rawText = clone.textContent || clone.innerText;
      
      // Fall back to aria-label if no text content
      if (!rawText || rawText.trim().length === 0) {
        rawText = element.getAttribute('aria-label');
      }
      
      // Fall back to title attribute
      if (!rawText || rawText.trim().length === 0) {
        rawText = element.getAttribute('title');
      }
    }
    
    // Clean the text (remove HTML, trim, remove breaks, ellipsis at 40 chars)
    return cleanText(rawText);
  }
  
  // ==========================================================================
  // CLICK HANDLER
  // ==========================================================================
  
  function handleClick(event, options = {}) {
    const element = event.target;
    const shouldSendImmediately = options.sendImmediately !== false;
    
    // First, check if element or parent has data-click-name
    const clickNameElement = element.closest('[data-click-name]');
    
    // Check if element is within a subscription-widget
    const subscriptionWidget = element.closest('.subscription-widget');
    
    // Try to find a trackable element
    let targetElement = element.closest('button, a, input[type="submit"], input[type="radio"], input[type="checkbox"], select');
    
    // If no trackable element found but we have a data-click-name parent, use that
    if (!targetElement && clickNameElement) {
      targetElement = clickNameElement;
    }
    
    // If still no trackable element but we're within subscription-widget, use the clicked element or closest clickable parent
    if (!targetElement && subscriptionWidget) {
      // Try to find the subscription-option or subscription-frequency container
      targetElement = element.closest('.subscription-option, .subscription-frequency, .subscription-frequency__button');
      // If still not found, use the clicked element itself
      if (!targetElement) {
        targetElement = element;
      }
    }
    
    // If still no trackable element, ignore the click
    if (!targetElement) {
      return;
    }
    
    const href = targetElement.getAttribute('href');
    const linkToUrl = href && !href.startsWith('#') ? href : null;
    
    const productHandle = linkToUrl ? getProductHandle(linkToUrl) : null;
    const variantId = productHandle ? getVariantId(linkToUrl) : null;
    const { sectionId, sectionHandle } = getSectionInfo(targetElement);
    
    // Get all click data attributes
    // Check both targetElement and original element (data attributes might be on either)
    const clickName = getClickName(targetElement) || getClickName(element);
    const clickId = getClickId(targetElement) || getClickId(element);
    const clickLinkName = getClickLinkName(targetElement) || getClickLinkName(element);
    const clickProduct = getClickProduct(targetElement) || getClickProduct(element);
    const clickVariant = getClickVariant(targetElement) || getClickVariant(element);
    const clickProductId = getClickProductId(targetElement) || getClickProductId(element);
    const clickVariantId = getClickVariantId(targetElement) || getClickVariantId(element);
    const elementId = getElementId(targetElement);
    
    // Try to get selected variant information for variant selectors
    let selectedVariant = null;
    const productIdFromElement = getDataAttribute(targetElement, 'product-id') || getDataAttribute(element, 'product-id');
    if (productIdFromElement && (targetElement.tagName === 'SELECT' || targetElement.type === 'radio')) {
      selectedVariant = getSelectedVariantForProduct(targetElement, productIdFromElement);
    }
    
    // Use custom_ prefix for custom parameters to avoid GA4 reserved names
    // Reserved names: page_location, page_path, page_title, etc.
    const eventData = {
      custom_click_name: clickName,
      custom_button_text: getElementText(targetElement),
      custom_element_type: targetElement.tagName.toLowerCase(),
      custom_element_role: targetElement.getAttribute('role'),
      page_path: window.location.pathname,
      page_title: document.title
    };
    
    // Always include these if they exist (even if empty string, but not null/undefined)
    if (clickId !== null && clickId !== undefined) eventData.custom_click_id = clickId;
    if (clickLinkName !== null && clickLinkName !== undefined) eventData.custom_click_link_name = clickLinkName;
    if (clickProduct !== null && clickProduct !== undefined) eventData.custom_click_product = clickProduct;
    if (clickVariant !== null && clickVariant !== undefined) eventData.custom_click_variant = clickVariant;
    if (clickProductId !== null && clickProductId !== undefined) eventData.custom_click_productid = clickProductId;
    if (clickVariantId !== null && clickVariantId !== undefined) eventData.custom_click_variantid = clickVariantId;
    if (elementId !== null && elementId !== undefined) eventData.custom_element_id = elementId;
    if (linkToUrl !== null && linkToUrl !== undefined) eventData.custom_link_url = linkToUrl;
    if (productHandle !== null && productHandle !== undefined) eventData.custom_product_handle = productHandle;
    if (variantId !== null && variantId !== undefined) eventData.custom_variant_id = variantId;
    if (sectionId !== null && sectionId !== undefined) eventData.custom_section_id = sectionId;
    if (sectionHandle !== null && sectionHandle !== undefined) eventData.custom_section_handle = sectionHandle;
    
    // Add selected variant information if available
    if (selectedVariant) {
      eventData.custom_click_variant = cleanText(selectedVariant.title);
      eventData.custom_click_variantid = selectedVariant.id;
    }
    
    // Debug logging to help troubleshoot missing attributes
    if (DEBUG) {
      // Safely get className (handles SVG elements where className is an object)
      const getClassName = (el) => {
        if (!el.className) return '';
        return typeof el.className === 'string' ? el.className : el.className.baseVal || '';
      };
      
      const targetClass = getClassName(targetElement);
      const elementClass = getClassName(element);
      
      const debugInfo = {
        'data-click-id': clickId,
        'data-click-name': clickName,
        'data-click-link-name': clickLinkName,
        'data-click-product': clickProduct,
        'data-click-variant': clickVariant,
        'data-click-productid': clickProductId,
        'data-click-variantid': clickVariantId,
        'targetElement': targetElement.tagName + (targetClass ? '.' + targetClass.split(' ')[0] : ''),
        'originalElement': element.tagName + (elementClass ? '.' + elementClass.split(' ')[0] : '')
      };
      console.log('[GA4] Debug - Data attributes found:', debugInfo);
    }
    
    // For SELECT elements, store the event and wait for user to finish selecting
    if (targetElement.tagName === 'SELECT' && shouldSendImmediately) {
      pendingSelectEvents.set(targetElement, eventData);
      if (DEBUG) {
        console.log('[GA4] SELECT change detected - will send after delay to capture final selection');
      }
      return;
    }

    // Skip element_click for upsell add buttons - these are tracked via upsell:added event
    const isUpsellButton = targetElement.classList.contains('yise-cart__upsells-add-btn') ||
        targetElement.closest('.yise-cart__upsells-add-btn') ||
        (clickName && clickName.startsWith('Cart - Add Upsell'));

    if (isUpsellButton) {
      if (DEBUG) {
        console.log('[GA4] Skipping upsell button element_click - tracked via upsell:added event');
      }
      return;
    }

    sendToGA4('element_click', eventData);
  }
  
  
  function handleSearchOpen(event) {
    const detail = event.detail || {};

    const eventData = {
      custom_search_trigger: detail.trigger || 'unknown',
      page_path: window.location.pathname,
      page_title: document.title
    };

    if (DEBUG) {
      console.log('[GA4] Search opened:', eventData);
    }

    sendToGA4('search_open', eventData);
  }

  /**
   * Handle search:close custom event
   * Fired when the search overlay is closed
   */
  function handleSearchClose(event) {
    const detail = event.detail || {};

    const eventData = {
      custom_search_term: detail.searchTerm || '',
      custom_had_results: detail.hadResults ? 'true' : 'false',
      custom_product_count: detail.productCount || 0,
      custom_article_count: detail.articleCount || 0,
      page_path: window.location.pathname,
      page_title: document.title
    };

    if (DEBUG) {
      console.log('[GA4] Search closed:', eventData);
    }

    sendToGA4('search_close', eventData);
  }

  /**
   * Handle search:query custom event
   * Fired when a search query is executed (after debounce)
   */
  function handleSearchQuery(event) {
    const detail = event.detail || {};
    const searchTerm = detail.searchTerm || '';

    const eventData = {
      custom_search_term: searchTerm,
      custom_search_term_length: searchTerm.length,
      page_path: window.location.pathname,
      page_title: document.title
    };

    if (DEBUG) {
      console.log('[GA4] Search query:', eventData);
    }

    sendToGA4('search_query', eventData);
  }

  /**
   * Handle search:results custom event
   * Fired when search results are loaded and displayed
   */
  function handleSearchResults(event) {
    const detail = event.detail || {};
    const productCount = detail.productCount || 0;
    const articleCount = detail.articleCount || 0;
    const totalResults = productCount + articleCount;

    const eventData = {
      custom_search_term: detail.searchTerm || '',
      custom_product_count: productCount,
      custom_article_count: articleCount,
      custom_total_results: totalResults,
      custom_has_results: totalResults > 0 ? 'true' : 'false',
      page_path: window.location.pathname,
      page_title: document.title
    };

    if (DEBUG) {
      console.log('[GA4] Search results loaded:', eventData);
    }

    sendToGA4('search_results_loaded', eventData);

    // Also fire search_no_results if applicable
    if (totalResults === 0 && detail.searchTerm) {
      const noResultsData = {
        custom_search_term: detail.searchTerm,
        custom_search_term_length: detail.searchTerm.length,
        page_path: window.location.pathname,
        page_title: document.title
      };

      if (DEBUG) {
        console.log('[GA4] Search no results:', noResultsData);
      }

      sendToGA4('search_no_results', noResultsData);
    }
  }

  /**
   * Handle search:result_click custom event
   * Fired when a user clicks on a search result
   */
  function handleSearchResultClick(event) {
    const detail = event.detail || {};

    const eventData = {
      custom_search_term: detail.searchTerm || '',
      custom_result_type: detail.resultType || 'unknown',
      custom_result_title: detail.resultTitle || '',
      custom_result_position: detail.resultPosition || 0,
      custom_result_id: detail.resultId || '',
      custom_result_url: detail.resultUrl || '',
      page_path: window.location.pathname,
      page_title: document.title
    };

    // Add variant info for products
    if (detail.resultVariant) {
      eventData.custom_result_variant = detail.resultVariant;
    }
    if (detail.resultVariantId) {
      eventData.custom_result_variant_id = detail.resultVariantId;
    }

    if (DEBUG) {
      console.log('[GA4] Search result clicked:', eventData);
    }

    sendToGA4('search_result_click', eventData);
  }

  // ==========================================================================
  // UPSELL ADD TO CART EVENT HANDLER
  // ==========================================================================

  /**
   * Handle upsell:added custom event
   * This is triggered after an upsell product is successfully added to cart
   */
  function handleUpsellAdded(event) {
    const detail = event.detail || {};

    const eventData = {
      custom_click_name: 'Cart - Upsell Added',
      custom_click_product: detail.productTitle || '',
      custom_click_productid: detail.productId || '',
      custom_click_variant: detail.variantTitle || '',
      custom_click_variantid: detail.variantId || '',
      custom_element_type: 'upsell',
      page_path: window.location.pathname,
      page_title: document.title
    };

    if (DEBUG) {
      console.log('[GA4] Upsell added event:', eventData);
    }

    sendToGA4('upsell_add_to_cart', eventData);
  }

  /**
   * Handle cross-sell:added custom event
   * This is triggered after a cross-sell product is successfully added to cart from PDP
   */
  function handleCrossSellAdded(event) {
    const detail = event.detail || {};

    const eventData = {
      custom_click_name: 'PDP - Cross Sell Added',
      custom_click_product: detail.productTitle || '',
      custom_click_productid: detail.productId || '',
      custom_click_variant: detail.variantTitle || '',
      custom_click_variantid: detail.variantId || '',
      custom_element_type: 'cross-sell',
      custom_source: detail.source || 'PDP Cross Sell',
      page_path: window.location.pathname,
      page_title: document.title
    };

    if (DEBUG) {
      console.log('[GA4] Cross-sell added event:', eventData);
    }

    sendToGA4('cross_sell_add_to_cart', eventData);
  }

  // ==========================================================================
  // GWP EVENT HANDLERS
  // ==========================================================================

  function normalizeGwpEventData(event) {
    const detail = event.detail || {};
    const eventData = {
      custom_gwp_offer_key: detail.custom_gwp_offer_key || '',
      custom_gwp_block_id: detail.custom_gwp_block_id || '',
      custom_gwp_promo_title: detail.custom_gwp_promo_title || '',
      custom_gwp_offer_type: detail.custom_gwp_offer_type || '',
      custom_gwp_program: detail.custom_gwp_program || '',
      custom_gwp_variant_id: detail.custom_gwp_variant_id || '',
      custom_gwp_product_id: detail.custom_gwp_product_id || '',
      custom_gwp_sku: detail.custom_gwp_sku || '',
      custom_gwp_product_title: detail.custom_gwp_product_title || '',
      custom_gwp_threshold_cents: detail.custom_gwp_threshold_cents || 0,
      custom_gwp_threshold_gap_cents: detail.custom_gwp_threshold_gap_cents || 0,
      custom_gwp_threshold_gap_band: detail.custom_gwp_threshold_gap_band || '',
      custom_gwp_qualified: detail.custom_gwp_qualified || 'false',
      custom_gwp_present: detail.custom_gwp_present || 'false',
      custom_gwp_quantity: detail.custom_gwp_quantity || 0,
      custom_cart_total_cents: detail.custom_cart_total_cents || 0,
      custom_cart_eligible_value_cents: detail.custom_cart_eligible_value_cents || 0,
      custom_cart_item_count: detail.custom_cart_item_count || 0,
      custom_cart_regular_item_count: detail.custom_cart_regular_item_count || 0,
      custom_has_subscription: detail.custom_has_subscription || 'false',
      custom_has_gift_card: detail.custom_has_gift_card || 'false',
      custom_currency: detail.custom_currency || '',
      custom_surface: detail.custom_surface || 'cart_drawer',
      custom_cart_token_hash: detail.custom_cart_token_hash || '',
      page_path: detail.page_path || window.location.pathname,
      page_title: detail.page_title || document.title
    };

    if (detail.custom_gwp_remove_reason) {
      eventData.custom_gwp_remove_reason = detail.custom_gwp_remove_reason;
    }
    if (detail.custom_gwp_pre_threshold_value_cents !== undefined) {
      eventData.custom_gwp_pre_threshold_value_cents = detail.custom_gwp_pre_threshold_value_cents;
    }
    if (detail.custom_gwp_increment_to_unlock_cents !== undefined) {
      eventData.custom_gwp_increment_to_unlock_cents = detail.custom_gwp_increment_to_unlock_cents;
    }
    if (detail.custom_gwp_time_to_unlock_seconds !== undefined) {
      eventData.custom_gwp_time_to_unlock_seconds = detail.custom_gwp_time_to_unlock_seconds;
    }
    if (detail.custom_gwp_cart_mutations_to_unlock !== undefined) {
      eventData.custom_gwp_cart_mutations_to_unlock = detail.custom_gwp_cart_mutations_to_unlock;
    }
    if (detail.custom_gwp_present_count !== undefined) {
      eventData.custom_gwp_present_count = detail.custom_gwp_present_count;
    }
    if (detail.custom_gwp_offer_keys) {
      eventData.custom_gwp_offer_keys = detail.custom_gwp_offer_keys;
    }
    if (detail.custom_checkout_token_hash) {
      eventData.custom_checkout_token_hash = detail.custom_checkout_token_hash;
    }
    if (detail.transaction_id) {
      eventData.transaction_id = detail.transaction_id;
    }

    return eventData;
  }

  function handleGwpOfferAvailable(event) {
    sendToGA4('gwp_offer_available', normalizeGwpEventData(event));
  }

  function handleGwpCartState(event) {
    sendToGA4('gwp_cart_state', normalizeGwpEventData(event));
  }

  function handleGwpThresholdReached(event) {
    sendToGA4('gwp_threshold_reached', normalizeGwpEventData(event));
  }

  function handleGwpThresholdLost(event) {
    sendToGA4('gwp_threshold_lost', normalizeGwpEventData(event));
  }

  function handleGwpGiftAdded(event) {
    sendToGA4('gwp_gift_added', normalizeGwpEventData(event));
  }

  function handleGwpGiftRemoved(event) {
    sendToGA4('gwp_gift_removed', normalizeGwpEventData(event));
  }

  function handleGwpCheckoutClick(event) {
    sendToGA4('gwp_checkout_click', normalizeGwpEventData(event));
  }

  const pendingTermsPrivacyEvents = [];

  function getCustomerPrivacyApi() {
    return window.Shopify && window.Shopify.customerPrivacy
      ? window.Shopify.customerPrivacy
      : null;
  }

  function isTermsPrivacyAnalyticsAllowed() {
    const customerPrivacy = getCustomerPrivacyApi();
    if (!customerPrivacy || typeof customerPrivacy.analyticsProcessingAllowed !== 'function') {
      return false;
    }

    return customerPrivacy.analyticsProcessingAllowed();
  }

  function isTermsPrivacyUserDataAllowed() {
    const customerPrivacy = getCustomerPrivacyApi();
    if (
      !isTermsPrivacyAnalyticsAllowed() ||
      !customerPrivacy ||
      typeof customerPrivacy.marketingAllowed !== 'function' ||
      typeof customerPrivacy.saleOfDataAllowed !== 'function'
    ) {
      return false;
    }

    return customerPrivacy.marketingAllowed() && customerPrivacy.saleOfDataAllowed();
  }

  function normalizeTermsPrivacyEventData(event) {
    const detail = event && event.detail ? event.detail : {};
    const eventData = {
      custom_notice_surface: detail.surface || 'unknown',
      custom_notice_version: detail.noticeVersion || 'unknown',
      page_path: detail.pagePath || window.location.pathname,
      page_title: detail.pageTitle || document.title
    };

    if (detail.closeMethod) {
      eventData.custom_close_method = detail.closeMethod;
    }

    return eventData;
  }

  function getTermsPrivacyUserData(event) {
    const detail = event && event.detail ? event.detail : {};
    const emailHash = detail.sha256EmailAddress || '';

    if (!/^[a-f0-9]{64}$/.test(emailHash)) {
      return null;
    }

    return {
      sha256_email_address: emailHash
    };
  }

  function sendTermsPrivacyEvent(eventName, event) {
    const pendingEvent = {
      eventName,
      eventData: normalizeTermsPrivacyEventData(event),
      userData: getTermsPrivacyUserData(event)
    };

    if (!isTermsPrivacyAnalyticsAllowed()) {
      pendingTermsPrivacyEvents.push(pendingEvent);
      return;
    }

    const userData = isTermsPrivacyUserDataAllowed() ? pendingEvent.userData : null;
    sendToGA4(pendingEvent.eventName, pendingEvent.eventData, userData);
  }

  function flushTermsPrivacyEvents() {
    const customerPrivacy = getCustomerPrivacyApi();
    if (!customerPrivacy || typeof customerPrivacy.analyticsProcessingAllowed !== 'function') {
      return;
    }

    if (!isTermsPrivacyAnalyticsAllowed()) {
      pendingTermsPrivacyEvents.length = 0;
      return;
    }

    const userDataIsAllowed = isTermsPrivacyUserDataAllowed();

    while (pendingTermsPrivacyEvents.length) {
      const pendingEvent = pendingTermsPrivacyEvents.shift();
      const userData = userDataIsAllowed ? pendingEvent.userData : null;
      sendToGA4(pendingEvent.eventName, pendingEvent.eventData, userData);
    }
  }

  function handleTermsPrivacyShown(event) {
    sendTermsPrivacyEvent('terms_privacy_notice_view', event);
  }

  function handleTermsPrivacyClosed(event) {
    sendTermsPrivacyEvent('terms_privacy_notice_close', event);
  }

  /**
   * Handle video-reels:start, video-reels:progress and video-reels:complete from
   * assets/video-reels.js, sent as GA4's recommended video events.
   */
  function handleVideoReelsEvent(eventName, event) {
    const detail = event.detail || {};

    const eventData = {
      video_title: detail.videoTitle || '',
      video_provider: 'shopify',
      video_percent: detail.percent,
      video_current_time: detail.currentTime,
      video_duration: detail.duration,
      custom_video_key: detail.videoKey || '',
      custom_video_playlist: detail.playlist || '',
      custom_video_position: detail.position,
      custom_element_type: 'video_reels',
      page_path: window.location.pathname,
      page_title: document.title
    };

    if (DEBUG) {
      console.log('[GA4] Video reels event:', eventName, eventData);
    }

    sendToGA4(eventName, eventData);
  }

  // ==========================================================================
  // INITIALIZATION
  // ==========================================================================

  function init() {
    // Set up click tracking
    document.addEventListener('click', handleClick, true);

    // Listen for custom upsell:added event
    document.addEventListener('upsell:added', handleUpsellAdded, true);

    // Listen for custom cross-sell:added event
    document.addEventListener('cross-sell:added', handleCrossSellAdded, true);

    // Listen for Video reels playback events
    document.addEventListener('video-reels:start', (event) => handleVideoReelsEvent('video_start', event), true);
    document.addEventListener('video-reels:progress', (event) => handleVideoReelsEvent('video_progress', event), true);
    document.addEventListener('video-reels:complete', (event) => handleVideoReelsEvent('video_complete', event), true);

    // Listen for GWP lifecycle events
    document.addEventListener('gwp:offer_available', handleGwpOfferAvailable, true);
    document.addEventListener('gwp:cart_state', handleGwpCartState, true);
    document.addEventListener('gwp:threshold_reached', handleGwpThresholdReached, true);
    document.addEventListener('gwp:threshold_lost', handleGwpThresholdLost, true);
    document.addEventListener('gwp:gift_added', handleGwpGiftAdded, true);
    document.addEventListener('gwp:gift_removed', handleGwpGiftRemoved, true);
    document.addEventListener('gwp:checkout_click', handleGwpCheckoutClick, true);

    document.addEventListener('terms-privacy:shown', handleTermsPrivacyShown, true);
    document.addEventListener('terms-privacy:closed', handleTermsPrivacyClosed, true);
    document.addEventListener('visitorConsentCollected', flushTermsPrivacyEvents);

    window.yiseTermsPrivacyAnalyticsReady = true;
    const queuedTermsPrivacyEvents = Array.isArray(window.yisePendingTermsPrivacyEvents)
      ? window.yisePendingTermsPrivacyEvents.splice(0)
      : [];
    queuedTermsPrivacyEvents.forEach((queuedEvent) => {
      if (!queuedEvent || !queuedEvent.eventName || !queuedEvent.detail) {
        return;
      }
      document.dispatchEvent(new CustomEvent(queuedEvent.eventName, { detail: queuedEvent.detail }));
    });

    // Listen for search events
    document.addEventListener('search:open', handleSearchOpen, true);
    document.addEventListener('search:close', handleSearchClose, true);
    document.addEventListener('search:query', handleSearchQuery, true);
    document.addEventListener('search:results', handleSearchResults, true);
    document.addEventListener('search:result_click', handleSearchResultClick, true);

    // Handle change events for form elements
    document.addEventListener('change', function(event) {
      const element = event.target;
      if (element.tagName === 'SELECT') {
        // For SELECT, store the event and set a timer to send after dropdown closes
        handleClick(event);
        
        // Clear any existing timer for this select
        if (selectEventTimers.has(element)) {
          clearTimeout(selectEventTimers.get(element));
        }
        
        // Set a new timer to send the event after a short delay
        // This allows the user to make multiple selections and only track the final one
        const timer = setTimeout(function() {
          if (pendingSelectEvents.has(element)) {
            let eventData = pendingSelectEvents.get(element);
            pendingSelectEvents.delete(element);
            selectEventTimers.delete(element);

            // For SELECT elements, get tracking data from the selected option
            const selectedOption = element.options[element.selectedIndex];
            if (selectedOption) {
              // Get tracking data from the selected option's data attributes
              const optionProductTitle = selectedOption.getAttribute('data-product-title');
              const optionProductId = selectedOption.getAttribute('data-product-id');
              const optionVariantTitle = selectedOption.getAttribute('data-variant-title') || selectedOption.getAttribute('data-display-label');
              const optionVariantId = selectedOption.getAttribute('data-variant-id');

              if (optionProductTitle) eventData.custom_click_product = optionProductTitle;
              if (optionProductId) eventData.custom_click_productid = optionProductId;
              if (optionVariantTitle) eventData.custom_click_variant = cleanText(optionVariantTitle);
              if (optionVariantId) eventData.custom_click_variantid = optionVariantId;
            }

            // Recalculate selected variant based on current state (for cross-sell dropdowns)
            const productIdFromElement = getDataAttribute(element, 'product-id');
            if (productIdFromElement) {
              const selectedVariant = getSelectedVariantForProduct(element, productIdFromElement);

              if (selectedVariant) {
                // Update event data with current selected variant
                eventData.custom_click_variant = cleanText(selectedVariant.title);
                eventData.custom_click_variantid = selectedVariant.id;
              }
            }

            // Skip element_click for upsell/cross-sell variant selects
            // These trigger add-to-cart and are tracked via upsell:added / cross-sell:added events
            if (element.classList.contains('yise-cart__upsells-variant-select') ||
                element.classList.contains('pdp-cross-sell__variant-select')) {
              if (DEBUG) {
                console.log('[GA4] Skipping SELECT element_click - tracked via add-to-cart event');
              }
              return;
            }

            // Only update button text if it was not already captured; dynamic selects may re-render
            if (!eventData.custom_button_text) {
              eventData.custom_button_text = getElementText(element);
            }

            if (DEBUG) {
              console.log('[GA4] SELECT final selection captured - sending event with variant:', eventData);
            }

            sendToGA4('element_click', eventData);
          }
        }, 500); // 500ms delay to ensure dropdown is closed and final selection is made
        
        selectEventTimers.set(element, timer);
        
      } else if (element.type === 'radio' || element.type === 'checkbox') {
        // For radio/checkbox, send immediately
        handleClick(event);
      }
    }, true);
  }
  
  // Wait for DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
